// Steuerzeile der Sonstige-Liste (Handoff Sonstige Kontakte §1, Screens 01–04):
// Suche · Kacheln/Liste · Auch Inaktive · Neuer Kontakt (Glas-Menü mit 4 Einträgen).
import { useEffect, useRef, useState, type KeyboardEvent as TastenEreignis } from 'react';
import { AddressBook, Key, List, MagnifyingGlass, Plus, SquaresFour, Users, Wrench, X, type Icon } from '@phosphor-icons/react';
import type { SonstigeAnsicht } from '../../../../lib/kontakte/ansicht';

const GLAS_KNOPF = { height: 44, borderRadius: 22 } as const;

export function SuchFeld({ wert, onChange }: { wert: string; onChange: (s: string) => void }) {
  const feld = useRef<HTMLInputElement>(null);
  return (
    <label data-tip="Kontakt suchen – Nach Name, Firma oder E-Mail" className="sk-glas sk-suche flex min-w-0 cursor-text items-center"
      style={{ ...GLAS_KNOPF, flex: '1 1 180px', gap: 10, padding: '0 14px' }}>
      <MagnifyingGlass weight="bold" size={16} className="shrink-0" />
      <input ref={feld} value={wert} onChange={e => onChange(e.target.value)} placeholder="Kontakt suchen…" aria-label="Kontakt suchen"
        className="min-w-0 flex-1 border-none bg-transparent outline-none"
        style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }} />
      {wert && (
        <button type="button" data-tip="Suche leeren" aria-label="Suche leeren" onClick={() => { onChange(''); feld.current?.focus(); }}
          className="sk-tipp flex shrink-0 cursor-pointer items-center justify-center"
          style={{ width: 22, height: 22, border: 'none', borderRadius: '50%', background: 'var(--fill3)', color: 'var(--ink2)' }}>
          <X weight="bold" size={11} />
        </button>
      )}
    </label>
  );
}

const ANSICHTEN: readonly { key: SonstigeAnsicht; label: string; icon: Icon; gewicht: 'fill' | 'bold'; tip: string }[] = [
  { key: 'kacheln', label: 'Kacheln', icon: SquaresFour, gewicht: 'fill', tip: 'Kacheln – Kontakte als Karten anzeigen' },
  { key: 'liste', label: 'Liste', icon: List, gewicht: 'bold', tip: 'Liste – Kontakte als kompakte Liste anzeigen' },
];

/** Segment Kacheln/Liste mit gleitendem Indikator. */
export function AnsichtSegment({ wert, onChange }: { wert: SonstigeAnsicht; onChange: (a: SonstigeAnsicht) => void }) {
  const tasten = (e: TastenEreignis) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    onChange(wert === 'kacheln' ? 'liste' : 'kacheln');
  };
  return (
    <div role="tablist" aria-label="Ansicht" onKeyDown={tasten} className="sk-glas relative flex shrink-0" style={{ ...GLAS_KNOPF, padding: 4, gap: 2 }}>
      <span aria-hidden className="sk-indikator absolute"
        style={{ top: 4, left: 4, width: 44, height: 36, borderRadius: 18, background: 'var(--acc-fill)', boxShadow: '0 4px 10px -4px var(--shade)', transform: wert === 'liste' ? 'translateX(46px)' : 'none' }} />
      {ANSICHTEN.map(a => {
        const an = a.key === wert;
        const I = a.icon;
        return (
          <button key={a.key} type="button" role="tab" aria-selected={an} aria-label={a.label} tabIndex={an ? 0 : -1} data-tip={a.tip} onClick={() => onChange(a.key)}
            className="relative flex cursor-pointer items-center justify-center"
            style={{ width: 44, height: 36, border: 'none', borderRadius: 18, background: 'transparent', color: an ? 'var(--acc-ink)' : 'var(--ink2)', transition: 'color .3s' }}>
            <I weight={a.gewicht} size={18} />
          </button>
        );
      })}
    </div>
  );
}

/** Glas-Knopf „Auch Inaktive“ mit Schalter. */
export function InaktivSchalter({ an, onChange }: { an: boolean; onChange: (an: boolean) => void }) {
  return (
    <button type="button" role="switch" aria-checked={an} onClick={() => onChange(!an)} data-tip="Auch Inaktive – Inaktive Kontakte zusätzlich anzeigen"
      className="sk-glas sk-hebt flex shrink-0 cursor-pointer items-center"
      style={{ ...GLAS_KNOPF, border: 'none', gap: 10, padding: '0 14px 0 16px', fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>
      Auch Inaktive
      <span aria-hidden className="relative" style={{ width: 40, height: 24, borderRadius: 12, background: an ? 'var(--ok)' : 'var(--fill3)', transition: 'background .25s' }}>
        <span className="sk-knauf absolute" style={{ top: 2, left: an ? 18 : 2, width: 20, height: 20, borderRadius: '50%', background: 'var(--sk-av-ink)', boxShadow: '0 2px 5px rgba(0,0,0,0.25)' }} />
      </span>
    </button>
  );
}

export interface NeuEintrag {
  key: 'mieter' | 'eigentuemer' | 'dienstleister' | 'kontakt';
  label: string;
  onClick: () => void;
}

const NEU_OPTIK: Record<NeuEintrag['key'], { icon: Icon; farbe: number; formular: string }> = {
  mieter: { icon: Users, farbe: 0, formular: 'Neuer Mieter' },
  eigentuemer: { icon: Key, farbe: 3, formular: 'Neuer Eigentümer' },
  dienstleister: { icon: Wrench, farbe: 2, formular: 'Neuer Dienstleister' },
  kontakt: { icon: AddressBook, farbe: 1, formular: 'Neuer Kontakt' },
};

/** „Neuer Kontakt“: + dreht 45°, Glas-Menü mit farbigen Icons. */
export function NeuMenue({ eintraege }: { eintraege: readonly NeuEintrag[] }) {
  const [offen, setOffen] = useState(false);
  const huelle = useRef<HTMLDivElement>(null);
  const knopf = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!offen) return;
    const aussen = (e: MouseEvent) => { if (!huelle.current?.contains(e.target as Node)) setOffen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOffen(false); knopf.current?.focus(); } };
    document.addEventListener('mousedown', aussen);
    window.addEventListener('keydown', esc);
    huelle.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    return () => { document.removeEventListener('mousedown', aussen); window.removeEventListener('keydown', esc); };
  }, [offen]);

  return (
    <div ref={huelle} className="relative shrink-0">
      <button ref={knopf} type="button" aria-haspopup="menu" aria-expanded={offen} onClick={() => setOffen(o => !o)}
        data-tip="Neuer Kontakt – Mieter, Eigentümer, Dienstleister oder sonstigen Kontakt anlegen"
        className="sk-akzent flex cursor-pointer items-center"
        style={{ ...GLAS_KNOPF, border: 'none', gap: 8, padding: '0 18px 0 14px', fontSize: 13, fontWeight: 800, color: 'var(--acc-ink)', background: 'var(--acc-fill)', boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.35), 0 10px 22px -10px var(--shade)' }}>
        <Plus weight="bold" size={15} className="sk-plus" style={{ transform: offen ? 'rotate(45deg)' : 'none' }} />
        Neuer Kontakt
      </button>
      {offen && (
        <div role="menu" aria-label="Neuer Kontakt" className="sk-menue-in absolute flex flex-col"
          style={{ right: 0, top: 52, zIndex: 20, width: 'min(260px, calc(100vw - 28px))', padding: 6, gap: 2, borderRadius: 20, background: 'var(--sheet)', backdropFilter: 'blur(30px) saturate(180%)', WebkitBackdropFilter: 'blur(30px) saturate(180%)', boxShadow: 'var(--glass-sh-hv)' }}>
          {eintraege.map(e => {
            const o = NEU_OPTIK[e.key];
            const I = o.icon;
            return (
              <button key={e.key} type="button" role="menuitem" data-tip={`${e.label} – Formular „${o.formular}“ öffnen`}
                onClick={() => { setOffen(false); e.onClick(); }}
                className="sk-menue-zeile flex cursor-pointer items-center text-left"
                style={{ gap: 11, padding: '8px 10px', border: 'none', borderRadius: 14, color: 'var(--ink)', outline: 'none' }}>
                <span aria-hidden className="flex shrink-0 items-center justify-center"
                  style={{ width: 32, height: 32, borderRadius: 10, color: 'var(--sk-av-ink)', background: `linear-gradient(155deg, var(--sk-av-${o.farbe}-a), var(--sk-av-${o.farbe}-b))`, boxShadow: `inset 0 1px 0 rgba(255,255,255,0.4), 0 4px 10px -4px var(--sk-av-${o.farbe}-b)` }}>
                  <I weight="fill" size={16} />
                </span>
                <span style={{ fontSize: 13.5, fontWeight: 700 }}>{e.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
